import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Keyboard,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import { useDeleteAccountMutation } from '@/store/api';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

const CloseIcon = SVG_COMPONENTS.CANCEL_X;
const TrashIcon = RESIDENTS_SVG_COMPONENTS.TRASH;

interface MobileDeleteAccountModalProps {
  visible: boolean;
  onClose: () => void;
  onAccountDeleted: () => void;
}

/**
 * Mobile Delete Account Modal
 * Full-screen modal for deleting user account
 * Based on Figma design "Delete account" (node-id=15074-39416)
 */
export const MobileDeleteAccountModal: React.FC<MobileDeleteAccountModalProps> = ({
  visible,
  onClose,
  onAccountDeleted,
}) => {
  const insets = useSafeAreaInsets();
  const { semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);
  const [reason, setReason] = useState('');
  const [deleteAccount, { isLoading }] = useDeleteAccountMutation();

  const resetForm = useCallback(() => {
    setReason('');
  }, []);

  const handleClose = useCallback(() => {
    Keyboard.dismiss();
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleDelete = useCallback(async () => {
    Keyboard.dismiss();
    
    try {
      const payload: any = {};
      if (reason.trim()) {
        payload.reason = reason.trim();
      }
      
      const resp = await deleteAccount(payload).unwrap();
      if ((resp as any)?.success) {
        resetForm();
        onAccountDeleted();
      } else {
        // Handle error - could add toast here
        console.error('Failed to delete account');
      }
    } catch (error) {
      console.error('Failed to delete account:', error);
    }
  }, [deleteAccount, reason, resetForm, onAccountDeleted]);

  // Bullet points for what user will lose
  const bulletPoints = [
    'Access to the account and any settings to manage it',
    'Information on community resources and events',
    'Property chat',
    "Your Wi-Fi and roommates' information",
  ];

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={false}
      onRequestClose={handleClose}
    >
      <View style={[styles.container, { paddingTop: insets.top }]}>
        <KeyboardAvoidingView
          style={styles.keyboardAvoidingView}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
        >
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Delete account</Text>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={handleClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              disabled={isLoading}
            >
              {CloseIcon && <CloseIcon width={16} height={16} color={semantic.text.primary} />}
            </TouchableOpacity>
          </View>

          {/* Scrollable Content */}
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={[styles.scrollContent, { paddingBottom: Math.max(insets.bottom, 24) + 80 }]}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            {/* Section 1: Sorry to see you go */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>We're sorry to see you go...</Text>
              <Text style={styles.sectionSubtitle}>When you delete your account, you'll lose:</Text>
            </View>

            {/* Bullet Points */}
            <View style={styles.bulletList}>
              {bulletPoints.map((point, index) => (
                <View key={index} style={styles.bulletItem}>
                  <Text style={styles.bulletDot}>•</Text>
                  <Text style={styles.bulletText}>{point}</Text>
                </View>
              ))}
            </View>

            {/* Gradient Divider */}
            <LinearGradient
              colors={['#EF303D', '#B53369', '#7B3794', '#5E39AA', '#413AC0', '#243CD6', '#153DE1', '#073EEC']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.gradientDivider}
            />

            {/* Section 2: Why deleting */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Why you're deleting your account?</Text>
              <Text style={styles.sectionSubtitle}>
                Your response is completely optional, but our team would appreciate it if you could help Resio improve.
              </Text>
            </View>

            {/* Reason Input */}
            <View style={styles.inputContainer}>
              <TextInput
                style={styles.input}
                value={reason}
                onChangeText={setReason}
                placeholder="Reason for deleting"
                placeholderTextColor={semantic.text.tertiary}
                multiline={false}
                autoCapitalize="sentences"
                autoCorrect={true}
                editable={!isLoading}
              />
            </View>

            {/* Delete Button */}
            <TouchableOpacity
              style={[styles.deleteButton, isLoading && styles.deleteButtonDisabled]}
              onPress={handleDelete}
              disabled={isLoading}
            >
              {isLoading ? (
                <ActivityIndicator size="small" color="#171717" />
              ) : (
                <>
                  <View style={styles.trashIconContainer}>
                    {TrashIcon && <TrashIcon width={14} height={16} color="#171717" />}
                  </View>
                  <Text style={styles.deleteButtonText}>Delete account</Text>
                </>
              )}
            </TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
};

const makeStyles = ({ semantic }: ThemeContextValue) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: semantic.background.secondary,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: semantic.border.secondary,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: semantic.text.primary,
    lineHeight: 27,
  },
  closeButton: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  section: {
    marginBottom: 16,
    gap: 4,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: semantic.text.primary,
    lineHeight: 30,
  },
  sectionSubtitle: {
    fontSize: 16,
    fontWeight: '400',
    color: semantic.text.tertiary,
    lineHeight: 24,
  },
  bulletList: {
    marginBottom: 32,
    gap: 12,
  },
  bulletItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  bulletDot: {
    fontSize: 14,
    fontWeight: '400',
    color: semantic.text.primary,
    lineHeight: 21,
    width: 21,
  },
  bulletText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: semantic.text.primary,
    lineHeight: 21,
  },
  gradientDivider: {
    height: 2,
    width: '100%',
    marginBottom: 32,
    borderRadius: 1,
  },
  inputContainer: {
    backgroundColor: 'rgba(238, 241, 244, 0.05)',
    borderRadius: 8,
    height: 40,
    paddingHorizontal: 16,
    justifyContent: 'center',
    marginBottom: 24,
  },
  input: {
    fontSize: 14,
    fontWeight: '400',
    color: semantic.text.primary,
    lineHeight: 17.5,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: semantic.interactive.danger,
    borderRadius: 8,
    height: 40,
    paddingHorizontal: 16,
    gap: 6,
    alignSelf: 'flex-start',
  },
  deleteButtonDisabled: {
    opacity: 0.6,
  },
  trashIconContainer: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#171717',
    lineHeight: 21,
  },
});

export default MobileDeleteAccountModal;
